import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import useUser from "./useUser";
import { MENU_SECTIONS, sectionForUrl, overrideLabel } from "@/config/menuGroups";

function getRuntimeBasePath(pathname: string): string {
  const explicit = (import.meta.env.VITE_PUBLIC_URL ?? "").replace(/\/$/, "")
  if (explicit) return explicit

  const knownRootRoutes = new Set([
    "",
    "login",
    "inicio",
    "usuarios",
    "clientes",
    "portabilidad",
    "consulta-uf",
    "batch",
    "preactivacion",
    "ventas",
    "menu",
    "ofertas",
    "datos-empresa",
    "msisdn",
    "facturacion",
    "liquidaciones",
    "empresas",
    "ecommerce",
    "ecommerce-products",
    "esim-profiles",
    "promociones-altan",
    "promociones",
    "banners",
    "portabilidades",
    "sms-campaigns",
    "push-campaigns",
    "medallas",
    "familias",
    "beneficios",
    "sms-templates",
    "perfil",
  ])

  const first = String(pathname || "").replace(/^\/+/, "").split("/")[0]?.toLowerCase() || ""
  if (!first || knownRootRoutes.has(first)) return ""
  return `/${first}`
}

export function pathMatches(link: string, pathname: string): boolean {
  if (!link || link === "#" || link === "/#") return false;
  const basePath = getRuntimeBasePath(pathname)
  const normalizedLink = link.replace(/^#/, "").trim();
  const fullPath = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const pathToMatch = basePath ? `${basePath}${fullPath}` : fullPath;
  const linkPath = normalizedLink.startsWith("/") ? normalizedLink : `/${normalizedLink}`;
  return (
    pathToMatch === linkPath ||
    pathToMatch.startsWith(linkPath + "/") ||
    (!!basePath && fullPath === linkPath)
  );
}

interface ApiMenuItem {
  id: number;
  name: string;
  url: string;
  icon: string;
  order: number;
  parent_menu_id: number | null;
  children: ApiMenuItem[];
}

export function normalizeText(value: string): string {
  return (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Filtra el men\u00fa (ya agrupado) por texto, insensible a acentos/may\u00fasculas.
 * - Un item se conserva si su label coincide o si alg\u00fan subitem coincide
 *   (en ese caso solo se conservan los subitems coincidentes).
 * - Un header de secci\u00f3n se conserva solo si su secci\u00f3n tiene coincidencias.
 * Pura y exportada para test unitario.
 */
export function filterMenuItems(items: MenuItem[], query: string): MenuItem[] {
  const q = normalizeText(query.trim());
  if (!q) return items;
  const result: MenuItem[] = [];
  let pendingHeader: MenuItem | null = null;
  for (const item of items) {
    if (item.isHeader) {
      pendingHeader = item;
      continue;
    }
    const labelMatches = normalizeText(item.label).includes(q);
    const matchingSubs = item.subItems.filter((sub) => normalizeText(sub.label).includes(q));
    if (!labelMatches && matchingSubs.length === 0) continue;
    if (pendingHeader) {
      result.push(pendingHeader);
      pendingHeader = null;
    }
    // Si solo coinciden subitems, mostrar el padre con esos subitems
    result.push(labelMatches ? item : { ...item, subItems: matchingSubs });
  }
  return result;
}

function resolveMenuIcon(label: string, link: string, apiIcon: string): string {
  const text = normalizeText(label);
  const url = normalizeText(link || "");
  const hasWords = (...words: string[]) => words.every((word) => text.includes(word));
  const safeApiIcon = (apiIcon || "").trim();
  const hasConsultaPerfil = text.includes("consulta") && (text.includes("perfil") || text.includes("linea"));
  const hasBatchOpsLabel =
    text.includes("batch") ||
    text.includes("lote") ||
    (text.includes("oper") && (text.includes("masiv") || text.includes("batch")));

  if (url.includes("/esim") || url.includes("/preactivacion")) return "ri-sim-card-2-line";
  if (url.includes("/menu") || url.includes("/consulta-uf") || hasWords("consulta", "perfil") || hasConsultaPerfil) return "ri-file-user-line";
  if (url.includes("/batch") || url.includes("/operaciones-batch") || hasBatchOpsLabel || hasWords("operacion", "masiv")) return "ri-database-2-line";
  if (url.includes("/usuarios") || text.includes("usuario")) return "ri-user-3-line";
  if (url.includes("/perfiles") || text.includes("perfil")) return "ri-file-user-line";
  if (url.includes("/rol") || text.includes("rol") || text.includes("permiso")) return "ri-shield-user-line";
  if (url.includes("/clientes") || text.includes("cliente")) return "ri-contacts-line";
  if (url.includes("/msisdn") || text.includes("msisdn") || text.includes("linea")) return "ri-smartphone-line";
  if (url.includes("/portabilidad") || text.includes("portabilidad") || text.includes("portar")) return "ri-repeat-2-line";
  if (url.includes("/promociones-altan") || text.includes("promocion")) return "ri-coupon-line";
  if (url.includes("/familias") || text.includes("familia")) return "ri-stack-line";
  if (url.includes("/beneficios") || text.includes("beneficio")) return "ri-movie-2-line";
  if (url.includes("/ofertas") || text.includes("oferta") || text.includes("producto")) return "ri-price-tag-3-line";
  if (url.includes("/ventas") || text.includes("venta")) return "ri-line-chart-line";
  if (url.includes("/facturacion") || text.includes("factura") || text.includes("facturacion") || text.includes("contab")) return "ri-calculator-line";
  if (url.includes("/empresas")) return "ri-community-line";
  if (url.includes("/datos-empresa") || text.includes("datos") && text.includes("empresa")) return "ri-building-2-line";
  if (text.includes("staff") || text.includes("equipo")) return "ri-team-line";
  if (url.includes("/ecommerce-products") || (text.includes("producto") && text.includes("ecommerce"))) return "ri-store-2-line";
  if (url.includes("/ecommerce") || text.includes("ecommerce") || text.includes("pedido")) return "ri-shopping-cart-2-line";
  if (url.includes("/banners") || text.includes("banner")) return "ri-image-line";
  if (text.includes("inicio") || text.includes("dashboard")) return "ri-home-4-line";

  return safeApiIcon.startsWith("ri-") ? safeApiIcon : "ri-question-line";
}

/**
 * Item de menú DERIVADO del API: solo datos puros, sin estado ni handlers.
 * El componente que lo renderiza decide qué hacer con clicks y qué menús abrir.
 *
 * Mantenemos `click` y `stateVariables` opcionales como NO-OP para compatibilidad
 * con TwoColumnLayout/HorizontalLayout legacy que aún los referencian aunque
 * solo VerticalLayout corre en producción.
 */
export interface MenuItem {
  id: string;
  isHeader?: boolean;
  label: string;
  icon: string;
  link: string;
  hasSubItems: boolean;
  subItems: MenuItem[];
  badge?: { name?: string; color?: string };
  isChildItem?: boolean;
  // Compat shims (no usados en VerticalLayout)
  click?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  stateVariables?: boolean;
}

/**
 * Agrupa los items raíz en secciones (config/menuGroups.ts) e intercala un
 * header `{ isHeader: true }` antes de cada grupo NO vacío — el render de
 * headers ya existe en VerticalLayout (li.menu-title).
 *
 * Pura y exportada para test unitario. Reglas:
 * - Sección sin items (filtrada por permisos) → no renderiza header.
 * - URL sin sección mapeada → "Otros" (warn solo en DEV).
 * - El orden relativo del backend (`order`) se conserva dentro de cada sección.
 */
export function groupMenuItems(items: MenuItem[]): MenuItem[] {
  if (items.length === 0) return items;
  const buckets = new Map<string, MenuItem[]>(MENU_SECTIONS.map((s) => [s.id, []]));
  items.forEach((item) => {
    let sectionId = sectionForUrl(item.link);
    // Fallback por label: "Inicio" va hasta arriba aunque su URL no esté
    // mapeada (algunas marcas lo mandan con url raíz o variantes)
    if (!sectionId && /^(inicio|home|dashboard|principal)$/.test(normalizeText(item.label).trim())) {
      sectionId = "standalone";
    }
    if (!sectionId || !buckets.has(sectionId)) {
      if (import.meta.env.DEV && !sectionId) {
        console.warn(`[menuGroups] URL de menú sin sección: "${item.link}" ("${item.label}") → Otros`);
      }
      sectionId = "otros";
    }
    buckets.get(sectionId)!.push(item);
  });

  const grouped: MenuItem[] = [];
  MENU_SECTIONS.forEach((section) => {
    const sectionItems = buckets.get(section.id)!;
    if (sectionItems.length === 0) return;
    if (section.label) {
      grouped.push({
        id: `section-${section.id}`,
        isHeader: true,
        label: section.label,
        icon: "",
        link: "#",
        hasSubItems: false,
        subItems: [],
      });
    }
    grouped.push(...sectionItems);
  });
  return grouped;
}

function useMenuItems(): MenuItem[] {
  const { menu: apiMenu, user } = useUser();
  // `menus`/`urls` son globales por install: en figou Figou e iGou recibirían el menú de Beneficios
  // de Be Online. Sólo se muestra si la marca tiene PLUUS (capacidad de crm/staffs/me).
  const conBeneficios = (user as Record<string, unknown> | null)?.beneficios_pluus === true;
  const { pathname: _pathname } = useLocation();

  return useMemo(() => {
    if (!apiMenu || apiMenu.length === 0) return [];
    const items = (apiMenu as ApiMenuItem[])
      .filter((item) => item.parent_menu_id === null)
      // Marca blanca vive en la capa superior (control plane), no en el CRM:
      // se filtra aunque el backend aún la mande en el menú del tenant.
      .filter((item) => !(item.url || "").includes("/marcas-blancas"))
      .filter((item) => conBeneficios || !(item.url || "").includes("/beneficios"))
      .sort((a, b) => a.order - b.order)
      .map((item): MenuItem => {
        const children = item.children && item.children.length > 0
          ? [...item.children].sort((a, b) => a.order - b.order)
          : [];
        const label = overrideLabel(item.url || "", item.name);
        return {
          id: `menu-${item.id}`,
          label,
          icon: resolveMenuIcon(label, item.url || "", item.icon),
          link: item.url || "#",
          hasSubItems: children.length > 0,
          isChildItem: false,
          // Compat no-op shims para layouts legacy
          click: () => {},
          stateVariables: false,
          subItems: children.map((child): MenuItem => {
            const childLabel = overrideLabel(child.url || "", child.name);
            return {
              id: `menu-${child.id}`,
              label: childLabel,
              icon: resolveMenuIcon(childLabel, child.url || "", child.icon),
              link: child.url || "#",
              hasSubItems: false,
              subItems: [],
              isChildItem: true,
              click: () => {},
              stateVariables: false,
            };
          }),
        };
      });
    return groupMenuItems(items);
  }, [apiMenu, conBeneficios]);
}

export default useMenuItems;
